/**
 * RunnerBar — one control surface for every Field Service record page.
 *
 * Design: `docs/superpowers/specs/2026-09-09-field-service-workflow-automation-design.md`
 * §7a. Dillan's complaint about AppSheet was not that the buttons were ugly, it was "our
 * team is always looking for the buttons, they move around" — so the rule this component
 * exists to enforce is fixed slots, variable contents, always labeled. Positions never move;
 * what fills them changes per screen. `RunnerBarContext` is the registry a page uses to fill
 * those slots; this file is purely presentational — it renders whatever
 * `useRunnerBarConfig()` hands it and knows nothing about tickets, statuses, generators, or
 * any lifecycle rule. That separation is deliberate: it is what lets the exact same bar sit
 * under a ticket (where zone 2 IS the transition engine), a generator (where zone 2 is
 * "create a ticket"), and a recurring schedule (where zone 2 has nothing to advance at all)
 * without this file growing a union of every module's domain.
 *
 * Mounted by `ModuleLayout`, exactly once per route — never imported by a page directly. See
 * `RunnerBarContext.tsx` for why a registry is the mechanism, and `ModuleLayout.tsx` for the
 * render site.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '../forms/Button';
import { toneClasses } from '../shared/statusTone';
import { useRunnerBarConfig } from '../../contexts/RunnerBarContext';
import type { RunnerBarAction, RunnerBarAdvance } from '../../types/runnerBar';

/**
 * The bar's fixed height, in pixels, and what `ModuleLayout`'s spacer (`RunnerBarSpacer`
 * below) reserves so the bar never covers the last row of a table or a page's final control.
 *
 * A constant rather than a measured height: the bar's internal typography and padding are
 * fixed by this file (a single-line, truncating identity block; one `size="lg"` — 44px —
 * button footprint in zone 2; fixed vertical padding), so its rendered height does not vary
 * with content the way a variable-height card would. Measuring it live would need a
 * `ResizeObserver` wired through the context for a height that never actually changes in
 * practice — real cost (untestable in `jsdom` without a mock, one more moving part) for no
 * real benefit. If a future zone ever grows to wrap onto a second line, this constant and the
 * layout below need to move together.
 */
export const RUNNER_BAR_HEIGHT_PX = 64;

/** Zone 3 renders at most this many `common` actions — the rest belong in `more`. */
const MAX_COMMON_ACTIONS = 3;

/**
 * Reserves layout space for the fixed-position bar, so page content's last row is never
 * covered by it. Renders nothing — and takes up no space — when no page has registered a
 * config, which is the normal state for every module other than Field Service and for the
 * Field Service pages that have not adopted the bar yet (§7a scopes it to four).
 */
export function RunnerBarSpacer(): ReactElement | null {
  const config = useRunnerBarConfig();
  if (!config) return null;
  return <div style={{ height: RUNNER_BAR_HEIGHT_PX }} aria-hidden="true" />;
}

/**
 * The bar itself. Renders `null` whenever no page on the current route has registered a
 * config — which, per `RunnerBarContext`, is every module except Field Service and every
 * Field Service page that has not adopted the bar. Mounting this unconditionally in
 * `ModuleLayout` therefore costs the other 12 modules nothing.
 */
export function RunnerBar(): ReactElement | null {
  const config = useRunnerBarConfig();

  if (!config) return null;

  const { identity, advance, common, more } = config;
  const commonActions = (common ?? []).slice(0, MAX_COMMON_ACTIONS);
  const moreActions = more ?? [];

  return (
    <div
      role="toolbar"
      aria-label={`Actions for ${identity.label}`}
      style={{ height: RUNNER_BAR_HEIGHT_PX }}
      className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-slate-300 bg-white
                 shadow-[0_-4px_16px_-6px_rgba(15,23,42,0.18)]"
    >
      {/*
        z-40, not z-50: the confirm dialog this bar's `action` buttons can open
        (`ConfirmTicketMoveDialog` and friends) renders at z-50 like every other modal in this
        app. The bar must sit BELOW that, or a confirm dialog opened from zone 2 would render
        underneath it. z-40 also matches the two existing fixed bottom bars in this codebase
        (`Assets/BatchActionBar.tsx`, the form runner's submit bar in
        `forms/public/layouts/MobileFormLayout.tsx`), so this is not a new idiom.

        DECISION REVERSED — this bar now LIFTS off the page (2px top edge + an upward
        shadow). It previously carried an explicit "recessed, not decorated" note citing
        section 7a of the design doc ("should read as part of the building, not as a
        feature"), and that is no longer the call: in use the bar disappeared into the page
        and people did not find the actions on it. Dillan asked for it to stand out
        (2026-09-14), reaffirmed after the prior decision was pointed out.

        The shadow is directional and shallow on purpose — enough to separate the bar from
        the content scrolling under it, not enough to become the brightest thing on screen.
        Zone 2's advance button is still the one element meant to draw the eye, and it keeps
        the only filled surface in the row.
      */}
      <div className="mx-auto flex h-full max-w-7xl flex-nowrap items-center gap-2 px-3 sm:gap-4 sm:px-6">
        <IdentityZone label={identity.label} meta={identity.meta} mono={identity.mono} />
        <AdvanceZone advance={advance} />
        <CommonZone actions={commonActions} />
        <MoreZone actions={moreActions} />
      </div>
    </div>
  );
}

function IdentityZone({
  label,
  meta,
  mono,
}: {
  label: string;
  meta?: string;
  mono?: boolean;
}) {
  return (
    // Not interactive — zone 1 answers "which record am I on" for free, it is never a button
    // or a link. `min-w-0` is required for the `truncate` below to actually clip inside a
    // flex row instead of forcing the row wider.
    <div data-testid="runner-bar-zone-identity" className="min-w-0 flex-1 sm:flex-none sm:w-48">
      <p className={`truncate text-sm font-bold text-slate-900 ${mono ? 'font-mono' : ''}`.trim()}>
        {label}
      </p>
      {/* Mobile drops the meta line — the plan explicitly allows this; zone 2 never moves
          because of it, since this zone's WIDTH (not its presence) is what zone 2 sits next
          to, and the width is fixed by the `sm:w-48` above regardless of whether this second
          line renders. */}
      {meta && <p className="hidden truncate text-xs text-slate-500 sm:block">{meta}</p>}
    </div>
  );
}

function AdvanceZone({ advance }: { advance: RunnerBarAdvance }) {
  return (
    <div data-testid="runner-bar-zone-advance" className="flex flex-none items-center justify-center">
      {advance.kind === 'loading' && (
        <div aria-hidden="true" className="h-11 w-36 animate-pulse rounded-xl bg-slate-200" />
      )}
      {advance.kind === 'action' && (
        <Button
          variant="primary"
          size="lg"
          title={advance.blurb}
          loading={advance.isPending}
          onClick={advance.onClick}
        >
          {advance.label}
        </Button>
      )}
      {advance.kind === 'reason' && (
        // Deliberately NOT a button. The design doc and the plan are both explicit that a
        // blocked or waiting state must say WHY rather than going grey — a disabled button
        // invites a click and explains nothing, which is the exact AppSheet-era failure this
        // bar exists to fix. No <button>, no onClick, no tabIndex: nothing here is focusable
        // or clickable, and it is readable as plain text by a screen reader with no extra
        // ARIA — this is not an announcement surface, so no role="status" / aria-live either
        // (that would make every reason change speak over the user).
        //
        // Tone vocabulary, not a raw colour: `blocked` (a move exists but is refused — dead
        // end for this record) borrows statusTone.ts's `blocked` tone (red); `waiting` (no
        // move exists by design — the normal middle of the pipeline) borrows `neutral`
        // rather than anything alarming, so the everyday case of "nothing to do yet" does not
        // read as an error.
        <span
          className={`inline-flex h-11 items-center rounded-xl px-4 text-sm font-bold ${toneClasses(
            advance.tone === 'blocked' ? 'blocked' : 'neutral'
          )}`}
        >
          {advance.text}
        </span>
      )}
    </div>
  );
}

function CommonZone({ actions }: { actions: readonly RunnerBarAction[] }) {
  if (actions.length === 0) return null;
  return (
    // Capped width + its own horizontal scroll: this zone's CONTENT can overflow on a narrow
    // phone without the zone's slot moving or the 4-zone row wrapping into a second line —
    // wrapping would reorder the zones visually, which is the one promise the design makes.
    <div
      data-testid="runner-bar-zone-common"
      // `p-1`: the scroll container clips on BOTH axes, and each button's `ring-1` outline is
      // drawn outside its box — without room for it the outline rendered broken.
      className="flex max-w-[45vw] flex-none items-center gap-2 overflow-x-auto p-1 sm:max-w-none"
    >
      {/*
        `brandTint`, not `secondary`. CLAUDE.md predicted this exact case when
        `secondary` was remapped from a brand-blue outline to neutral
        (`bg-white ring-1 ring-slate-200`): "a button that reads as
        demoted-to-invisible on its page is a candidate for `brandTint`". On a
        white bar, a slate-200 ring is the invisible edge Dillan reported on the
        Edit button — these are in-brand actions on the record, not the
        Navigate/View/Cancel neutrals `secondary` is defined for.
      */}
      {actions.map((action) => (
        <Button
          key={action.key}
          variant="brandTint"
          size="lg"
          disabled={action.disabled}
          title={action.disabled ? action.disabledReason : undefined}
          onClick={action.onClick}
        >
          {action.icon && <action.icon className="h-4 w-4" />}
          {action.label}
        </Button>
      ))}
    </div>
  );
}

function MoreZone({ actions }: { actions: readonly RunnerBarAction[] }) {
  const [open, setOpen] = useState(false);
  // `Button` is a plain function component (no `forwardRef`), and it is out of this file's
  // scope to change that — so the position/outside-click anchor is a wrapping `<div>` around
  // the trigger rather than a ref on `Button` itself.
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; bottom: number } | null>(null);

  // Same portal-plus-fixed-position approach as `StageMenu` (this module's other
  // portal-based popover), opening UPWARD from the trigger since this bar lives at the very
  // bottom of the viewport and a downward menu would run off-screen.
  useEffect(() => {
    if (!open) return;
    function computePosition() {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 224;
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      setPosition({ left, bottom: window.innerHeight - rect.top + 8 });
    }
    computePosition();
    window.addEventListener('resize', computePosition);
    return () => window.removeEventListener('resize', computePosition);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (actions.length === 0) return null;

  return (
    <div data-testid="runner-bar-zone-more" className="flex flex-none items-center" ref={triggerRef}>
      <Button
        variant="secondary"
        size="lg"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizontal className="h-4 w-4" />
      </Button>

      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-40 w-56 rounded-xl bg-white py-1 shadow-lg ring-1 ring-slate-200"
            style={{ left: position.left, bottom: position.bottom }}
          >
            {actions.map((action) => (
              // Plain menu-row buttons, not <Button> — same idiom StageMenu uses for its
              // options. <Button> is styled as a standalone CTA (padding, its own surface);
              // a dense list of menu rows conventionally is not one, and nothing in this
              // menu is a primary action regardless of styling.
              <button
                key={action.key}
                type="button"
                role="menuitem"
                disabled={action.disabled}
                title={action.disabled ? action.disabledReason : undefined}
                onClick={() => {
                  setOpen(false);
                  action.onClick();
                }}
                className={`flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm font-semibold transition-colors ${
                  action.disabled
                    ? 'cursor-not-allowed text-slate-400'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-[#007ACC]'
                }`}
              >
                {action.icon && <action.icon className="h-4 w-4 shrink-0" />}
                {action.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
